import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useStore } from '@tanstack/react-store';
import {
  DataTableSkeleton,
  Dropdown,
  FileUploaderDropContainer,
  FileUploaderItem,
  InlineNotification,
  Link,
  SkeletonPlaceholder,
  TextInput,
} from '@carbon/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import moment from 'moment';
import { PriceCardDeck } from 'components/price-card';
import { DatePicker } from 'components/date-picker/date-picker';
import { PriceCurvePreviewChart } from 'components/price-curve-chart/price-curve-preview-chart';
import type { HistoricRange } from 'components/price-curve-chart/types';
import {
  sourcesAtom,
  sourcesLoadingAtom,
  fetchSourcesAtom,
} from 'pages/forward-price-curve/atoms';
import {
  buildChartData,
  buildPriceCards,
  DECK_TYPE_ITEMS,
  FALLBACK_TYPE_ITEMS,
  METHOD_TYPE_ITEMS,
  STRIP_DATE_MODE_ITEMS,
  UPLOAD_FALLBACK_TYPE_ITEMS,
} from '../utils';
import {
  DECK_TYPE_RELATIVE,
  FALLBACK_DISCRETE_PRICE,
  FALLBACK_HOLD_LAST_STRIP,
  FALLBACK_HOLD_LAST_VALUE,
  METHOD_CUSTOM_UPLOAD,
  METHOD_FLAT,
  METHOD_MODIFIED_STRIP,
  METHOD_STRIP,
  STRIP_MODE_AS_OF,
  STRIP_MODE_MOST_RECENT,
  type DeckType,
  type FallbackType,
  type MethodType,
  type StripDateMode,
} from '../types';
import { PriceDeckScheduleTable } from '../price-deck-schedule-table';
import { PriceDeckFormCtx } from './context';
import {
  previewPriceDeckLoadableAtom,
  stripReferenceLoadableAtom,
  uploadPriceDeckFileAtom,
} from './atoms';
import type { PriceDeckFormValues } from './types';
import styles from './price-deck-form.module.scss';

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

const PriceDeckForm: FC = () => {
  const { form } = useContext(PriceDeckFormCtx)!;
  const values = useStore(
    form.store,
    (s: { values: PriceDeckFormValues }) => s.values
  );

  // Strip price sources (shared atom from forward-price-curve)
  const sources = useAtomValue(sourcesAtom);
  const sourcesLoading = useAtomValue(sourcesLoadingAtom);
  const triggerFetchSources = useSetAtom(fetchSourcesAtom);

  // File upload atom
  const triggerUpload = useSetAtom(uploadPriceDeckFileAtom);

  // Preview atom
  const [previewState, triggerPreview] = useAtom(previewPriceDeckLoadableAtom);
  const preview = previewState.state === 'hasData' ? previewState.data : null;
  const previewError =
    previewState.state === 'hasError'
      ? (previewState.error as Error).message
      : null;
  const previewLoading = previewState.state === 'loading';

  // Strip reference (raw strip prices for modified strip dotted lines)
  const [stripRefState, triggerStripRef] = useAtom(stripReferenceLoadableAtom);
  const stripReference =
    stripRefState.state === 'hasData' ? stripRefState.data : null;

  // Historic data visibility on the chart (only strip/modified_strip).
  // Backend always returns full history; radio buttons just control the x-axis view.
  const [historicMonths, setHistoricMonths] = useState<HistoricRange>(0);

  const [uploadStatus, setUploadStatus] = useState<{
    name: string;
    status: 'uploading' | 'complete' | 'error';
    error?: string;
  } | null>(
    values.originalFilename
      ? { name: values.originalFilename, status: 'complete' }
      : null
  );

  // Debounced preview trigger
  const debouncedPreview = useMemo(
    () => debounce((v: PriceDeckFormValues) => triggerPreview(v), 500),
    [triggerPreview]
  );
  useEffect(() => () => debouncedPreview.cancel(), [debouncedPreview]);

  // Fetch strip price sources on mount
  useEffect(() => {
    triggerFetchSources();
  }, [triggerFetchSources]);

  // Set default strip source from fetched sources
  useEffect(() => {
    if (sources.length > 0 && !values.stripSource) {
      form.setFieldValue('stripSource', sources[0].source);
    }
  }, [sources, values.stripSource, form]);

  // ─── Preview Logic ───

  const canPreview = useMemo(() => {
    const hasFallbackPrices =
      (values.oilFallbackType !== FALLBACK_DISCRETE_PRICE ||
        values.oilFlatPrice !== null) &&
      (values.gasFallbackType !== FALLBACK_DISCRETE_PRICE ||
        values.gasFlatPrice !== null);

    if (values.methodType === METHOD_FLAT) {
      return values.oilFlatPrice !== null && values.gasFlatPrice !== null;
    }
    if (values.methodType === METHOD_STRIP) {
      return (
        values.stripDateMode !== STRIP_MODE_AS_OF || !!values.stripPriceDate
      );
    }
    if (values.methodType === METHOD_MODIFIED_STRIP) {
      if (values.stripDateMode === STRIP_MODE_AS_OF && !values.stripPriceDate)
        return false;
      if (values.oilScaleFactor === null || values.gasScaleFactor === null)
        return false;
      return hasFallbackPrices;
    }
    if (values.methodType === METHOD_CUSTOM_UPLOAD) {
      if (!values.blobPath) return false;
      return hasFallbackPrices;
    }
    return false;
  }, [
    values.methodType,
    values.oilFlatPrice,
    values.gasFlatPrice,
    values.stripDateMode,
    values.stripPriceDate,
    values.oilScaleFactor,
    values.gasScaleFactor,
    values.oilFallbackType,
    values.gasFallbackType,
    values.blobPath,
  ]);

  // Debounced preview trigger — only fires when pricing-relevant fields change
  const previewKey = JSON.stringify({
    mt: values.methodType,
    op: values.oilFlatPrice,
    gp: values.gasFlatPrice,
    sdm: values.stripDateMode,
    ss: values.stripSource,
    spd: values.stripPriceDate,
    osd: values.oilStripDuration,
    osf: values.oilScaleFactor,
    oft: values.oilFallbackType,
    gsd: values.gasStripDuration,
    gsf: values.gasScaleFactor,
    gft: values.gasFallbackType,
    bp: values.blobPath,
    dt: values.deckType,
    ad: values.anchorDate,
  });

  useEffect(() => {
    if (!canPreview) return;
    debouncedPreview(values);
    if (values.methodType === METHOD_MODIFIED_STRIP) {
      triggerStripRef(values);
    }
    return () => debouncedPreview.cancel();
  }, [canPreview, previewKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handlers ───

  const handleMethodChange = useCallback(
    ({
      selectedItem,
    }: {
      selectedItem: { id: string; label: string } | null;
    }) => {
      if (!selectedItem) return;
      form.setFieldValue('methodType', selectedItem.id as MethodType);
      if (selectedItem.id === METHOD_CUSTOM_UPLOAD) {
        form.setFieldValue('oilFallbackType', FALLBACK_HOLD_LAST_VALUE);
        form.setFieldValue('gasFallbackType', FALLBACK_HOLD_LAST_VALUE);
      } else {
        form.setFieldValue('oilFallbackType', FALLBACK_HOLD_LAST_STRIP);
        form.setFieldValue('gasFallbackType', FALLBACK_HOLD_LAST_STRIP);
      }
    },
    [form]
  );

  const handleStripDateModeChange = useCallback(
    ({
      selectedItem,
    }: {
      selectedItem: { id: string; label: string } | null;
    }) => {
      if (!selectedItem) return;
      form.setFieldValue('stripDateMode', selectedItem.id as StripDateMode);
      if (selectedItem.id === STRIP_MODE_MOST_RECENT) {
        form.setFieldValue('stripPriceDate', null);
      }
    },
    [form]
  );

  const handleFileUpload = useCallback(
    async (
      _event: React.SyntheticEvent<HTMLElement>,
      { addedFiles }: { addedFiles: any[] }
    ) => {
      const file = addedFiles?.[0];
      if (!file) return;

      setUploadStatus({ name: file.name, status: 'uploading' });
      try {
        const result = await triggerUpload(file);
        form.setFieldValue('blobPath', result.blob_path);
        form.setFieldValue('originalFilename', result.original_filename);
        setUploadStatus({ name: result.original_filename, status: 'complete' });
      } catch (err) {
        setUploadStatus({
          name: file.name,
          status: 'error',
          error: err instanceof Error ? err.message : 'Upload failed',
        });
        form.setFieldValue('blobPath', null);
        form.setFieldValue('originalFilename', null);
      }
    },
    [form, triggerUpload]
  );

  const handleFileDelete = useCallback(() => {
    setUploadStatus(null);
    form.setFieldValue('blobPath', null);
    form.setFieldValue('originalFilename', null);
  }, [form]);

  // ─── Derived Values ───

  const selectedMethod =
    METHOD_TYPE_ITEMS.find((m) => m.id === values.methodType) ??
    METHOD_TYPE_ITEMS[0];
  const selectedStripDateMode =
    STRIP_DATE_MODE_ITEMS.find((m) => m.id === values.stripDateMode) ??
    STRIP_DATE_MODE_ITEMS[0];
  const stripSourceItems = useMemo(
    () => sources.map((s) => ({ id: s.source, label: s.source })),
    [sources]
  );
  const selectedStripSource =
    stripSourceItems.find((m) => m.id === values.stripSource) ??
    stripSourceItems[0] ??
    null;
  const lastClosingDate =
    sources.find((s) => s.source === values.stripSource)?.lastClosingDate ?? '';
  const selectedOilFallback =
    FALLBACK_TYPE_ITEMS.find((m) => m.id === values.oilFallbackType) ??
    FALLBACK_TYPE_ITEMS[0];
  const selectedGasFallback =
    FALLBACK_TYPE_ITEMS.find((m) => m.id === values.gasFallbackType) ??
    FALLBACK_TYPE_ITEMS[0];
  const selectedDeckType =
    DECK_TYPE_ITEMS.find((m) => m.id === values.deckType) ?? DECK_TYPE_ITEMS[0];
  const fallbackItems =
    values.methodType === METHOD_CUSTOM_UPLOAD
      ? UPLOAD_FALLBACK_TYPE_ITEMS
      : FALLBACK_TYPE_ITEMS;
  const selectedUploadOilFallback =
    fallbackItems.find((m) => m.id === values.oilFallbackType) ??
    fallbackItems[0];
  const selectedUploadGasFallback =
    fallbackItems.find((m) => m.id === values.gasFallbackType) ??
    fallbackItems[0];

  const priceCards = useMemo(() => buildPriceCards(preview?.stats), [preview]);

  const { chartSeries, chartDates } = useMemo(
    () =>
      buildChartData(
        preview,
        values.methodType === METHOD_MODIFIED_STRIP ? stripReference : null
      ),
    [preview, stripReference, values.methodType]
  );

  // ─── Helper to set a numeric field ───
  const setNumericField = useCallback(
    (field: keyof PriceDeckFormValues, val: string) => {
      form.setFieldValue(field, val === '' ? null : parseFloat(val));
    },
    [form]
  );

  // ─── Render ───

  return (
    <div className={styles.page}>
      <section className={styles.whitePanel}>
        <h3 className={styles.sectionTitle}>Deck Configuration</h3>
        <div className={styles.configRow}>
          <TextInput
            id="deck-name"
            labelText="Deck Name"
            placeholder="e.g. MTM Base Case, Acquisition Strip Q1 2026"
            value={values.name}
            light
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              form.setFieldValue('name', e.target.value)
            }
          />
          <TextInput
            id="deck-description"
            labelText="Description (optional)"
            placeholder="When should this deck be used?"
            value={values.description}
            light
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              form.setFieldValue('description', e.target.value)
            }
          />
          <Dropdown
            id="pricing-method"
            titleText="Pricing Method"
            label=""
            items={METHOD_TYPE_ITEMS}
            selectedItem={selectedMethod}
            itemToString={(item: { id: string; label: string }) =>
              item?.label ?? ''
            }
            onChange={handleMethodChange}
            light
          />
        </div>

        {values.methodType === METHOD_FLAT && (
          <div className={styles.priceRow}>
            <TextInput
              id="oil-flat-price"
              labelText="Oil Price ($/BBL)"
              type="number"
              value={
                values.oilFlatPrice !== null ? String(values.oilFlatPrice) : ''
              }
              light
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNumericField('oilFlatPrice', e.target.value)
              }
            />
            <TextInput
              id="gas-flat-price"
              labelText="Gas Price ($/MMBTU)"
              type="number"
              value={
                values.gasFlatPrice !== null ? String(values.gasFlatPrice) : ''
              }
              light
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNumericField('gasFlatPrice', e.target.value)
              }
            />
          </div>
        )}

        {(values.methodType === METHOD_STRIP ||
          values.methodType === METHOD_MODIFIED_STRIP) && (
          <div className={styles.stripRow}>
            <Dropdown
              id="strip-date-mode"
              titleText={`Strip Date${
                values.stripSource &&
                values.stripDateMode === STRIP_MODE_MOST_RECENT
                  ? ` (${lastClosingDate})`
                  : ''
              }`}
              label=""
              items={STRIP_DATE_MODE_ITEMS}
              selectedItem={selectedStripDateMode}
              itemToString={(item: { id: string; label: string }) =>
                item?.label ?? ''
              }
              onChange={handleStripDateModeChange}
              light
            />
            <Dropdown
              id="strip-source"
              titleText="Source"
              label=""
              items={stripSourceItems}
              selectedItem={selectedStripSource}
              disabled={sourcesLoading}
              itemToString={(item: { id: string; label: string }) =>
                item?.label ?? ''
              }
              onChange={({
                selectedItem,
              }: {
                selectedItem: { id: string; label: string } | null;
              }) => {
                if (selectedItem)
                  form.setFieldValue('stripSource', selectedItem.id);
              }}
              light
            />
            {values.stripDateMode === STRIP_MODE_AS_OF && (
              <DatePicker
                id="strip-price-date"
                labelText="As-Of Date"
                value={values.stripPriceDate ?? undefined}
                onChange={(dates: Date[]) => {
                  if (dates.length > 0 && dates[0]) {
                    form.setFieldValue(
                      'stripPriceDate',
                      moment(dates[0]).format('YYYY-MM-DD')
                    );
                  }
                }}
                light
              />
            )}
          </div>
        )}

        {values.methodType === METHOD_MODIFIED_STRIP && (
          <div className={styles.modifiedStripRow}>
            <TextInput
              id="oil-strip-duration"
              labelText="Oil Strip Duration (mo)"
              type="number"
              value={
                values.oilStripDuration !== null
                  ? String(values.oilStripDuration)
                  : ''
              }
              light
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNumericField('oilStripDuration', e.target.value)
              }
            />
            <TextInput
              id="oil-scale-factor"
              labelText="Oil Scale Factor (%)"
              type="number"
              value={
                values.oilScaleFactor !== null
                  ? String(values.oilScaleFactor)
                  : ''
              }
              light
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNumericField('oilScaleFactor', e.target.value)
              }
            />
            <Dropdown
              id="oil-fallback"
              titleText="Oil Fallback"
              label=""
              items={FALLBACK_TYPE_ITEMS}
              selectedItem={selectedOilFallback}
              itemToString={(item: { id: string; label: string }) =>
                item?.label ?? ''
              }
              onChange={({
                selectedItem,
              }: {
                selectedItem: { id: string; label: string } | null;
              }) => {
                if (selectedItem)
                  form.setFieldValue(
                    'oilFallbackType',
                    selectedItem.id as FallbackType
                  );
              }}
              light
            />
            {values.oilFallbackType === FALLBACK_DISCRETE_PRICE && (
              <TextInput
                id="oil-fallback-price"
                labelText="Oil $/BBL"
                type="number"
                value={
                  values.oilFlatPrice !== null
                    ? String(values.oilFlatPrice)
                    : ''
                }
                light
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNumericField('oilFlatPrice', e.target.value)
                }
              />
            )}
            <div className={styles.verticalDivider} />
            <TextInput
              id="gas-strip-duration"
              labelText="Gas Strip Duration (mo)"
              type="number"
              value={
                values.gasStripDuration !== null
                  ? String(values.gasStripDuration)
                  : ''
              }
              light
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNumericField('gasStripDuration', e.target.value)
              }
            />
            <TextInput
              id="gas-scale-factor"
              labelText="Gas Scale Factor (%)"
              type="number"
              value={
                values.gasScaleFactor !== null
                  ? String(values.gasScaleFactor)
                  : ''
              }
              light
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNumericField('gasScaleFactor', e.target.value)
              }
            />
            <Dropdown
              id="gas-fallback"
              titleText="Gas Fallback"
              label=""
              items={FALLBACK_TYPE_ITEMS}
              selectedItem={selectedGasFallback}
              itemToString={(item: { id: string; label: string }) =>
                item?.label ?? ''
              }
              onChange={({
                selectedItem,
              }: {
                selectedItem: { id: string; label: string } | null;
              }) => {
                if (selectedItem)
                  form.setFieldValue(
                    'gasFallbackType',
                    selectedItem.id as FallbackType
                  );
              }}
              light
            />
            {values.gasFallbackType === FALLBACK_DISCRETE_PRICE && (
              <TextInput
                id="gas-fallback-price"
                labelText="Gas $/MMBTU"
                type="number"
                value={
                  values.gasFlatPrice !== null
                    ? String(values.gasFlatPrice)
                    : ''
                }
                light
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNumericField('gasFlatPrice', e.target.value)
                }
              />
            )}
          </div>
        )}

        {values.methodType === METHOD_CUSTOM_UPLOAD && (
          <>
            <div className={styles.uploadRow}>
              <div className={styles.uploadZone}>
                <div className={styles.uploadLabel}>
                  Price Schedule (
                  <Link
                    href={
                      values.deckType === DECK_TYPE_RELATIVE
                        ? '/samples/price_deck_relative_months.csv'
                        : '/samples/price_deck_calendar_dates.csv'
                    }
                    download
                  >
                    download sample
                  </Link>
                  )
                </div>
                <FileUploaderDropContainer
                  labelText="Drag a file here or click to browse (.csv, .xlsx)"
                  accept={['.csv', '.xlsx']}
                  onAddFiles={handleFileUpload}
                />
                {uploadStatus && (
                  <FileUploaderItem
                    name={uploadStatus.name}
                    status={
                      uploadStatus.status === 'error'
                        ? 'edit'
                        : uploadStatus.status === 'uploading'
                          ? 'uploading'
                          : 'complete'
                    }
                    invalid={uploadStatus.status === 'error'}
                    errorBody={uploadStatus.error}
                    onDelete={handleFileDelete}
                  />
                )}
              </div>
            </div>
            <div className={styles.uploadConfigRow}>
              <Dropdown
                id="deck-type"
                titleText="Deck Type"
                label=""
                items={DECK_TYPE_ITEMS}
                selectedItem={selectedDeckType}
                itemToString={(item: { id: string; label: string }) =>
                  item?.label ?? ''
                }
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: { id: string; label: string } | null;
                }) => {
                  if (selectedItem)
                    form.setFieldValue('deckType', selectedItem.id as DeckType);
                }}
                light
              />
              {values.deckType === DECK_TYPE_RELATIVE && (
                <DatePicker
                  id="anchor-date"
                  labelText="Anchor Date"
                  value={values.anchorDate ?? undefined}
                  onChange={(dates: Date[]) => {
                    if (dates.length > 0 && dates[0]) {
                      const dt = dates[0];
                      form.setFieldValue(
                        'anchorDate',
                        moment(dt).startOf('month').format('YYYY-MM-DD')
                      );
                    }
                  }}
                  light
                />
              )}
              <Dropdown
                id="upload-oil-fallback"
                titleText="Oil Fallback"
                label=""
                items={fallbackItems}
                selectedItem={selectedUploadOilFallback}
                itemToString={(item: { id: string; label: string }) =>
                  item?.label ?? ''
                }
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: { id: string; label: string } | null;
                }) => {
                  if (selectedItem)
                    form.setFieldValue(
                      'oilFallbackType',
                      selectedItem.id as FallbackType
                    );
                }}
                light
              />
              {values.oilFallbackType === FALLBACK_DISCRETE_PRICE && (
                <TextInput
                  id="upload-oil-fallback-price"
                  labelText="Oil $/BBL"
                  type="number"
                  value={
                    values.oilFlatPrice !== null
                      ? String(values.oilFlatPrice)
                      : ''
                  }
                  light
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setNumericField('oilFlatPrice', e.target.value)
                  }
                />
              )}
              <Dropdown
                id="upload-gas-fallback"
                titleText="Gas Fallback"
                label=""
                items={fallbackItems}
                selectedItem={selectedUploadGasFallback}
                itemToString={(item: { id: string; label: string }) =>
                  item?.label ?? ''
                }
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: { id: string; label: string } | null;
                }) => {
                  if (selectedItem)
                    form.setFieldValue(
                      'gasFallbackType',
                      selectedItem.id as FallbackType
                    );
                }}
                light
              />
              {values.gasFallbackType === FALLBACK_DISCRETE_PRICE && (
                <TextInput
                  id="upload-gas-fallback-price"
                  labelText="Gas $/MMBTU"
                  type="number"
                  value={
                    values.gasFlatPrice !== null
                      ? String(values.gasFlatPrice)
                      : ''
                  }
                  light
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setNumericField('gasFlatPrice', e.target.value)
                  }
                />
              )}
            </div>
          </>
        )}
      </section>

      {previewError && (
        <InlineNotification
          kind="warning"
          title="No data available"
          subtitle={previewError}
          hideCloseButton
        />
      )}

      <div className={styles.content}>
        {preview && priceCards.length > 0 && (
          <section className={styles.sectionPanel}>
            <h3 className={styles.sectionTitle}>Current Prices</h3>
            <PriceCardDeck items={priceCards} />
          </section>
        )}

        <section className={styles.whitePanel}>
          <h3 className={styles.sectionTitle}>Price Curve Preview</h3>
          {previewLoading ? (
            <SkeletonPlaceholder className={styles.chartSkeleton} />
          ) : chartSeries ? (
            <PriceCurvePreviewChart
              series={chartSeries}
              dates={chartDates}
              filterMode="projection"
              showHistoricControls={
                values.methodType === METHOD_STRIP ||
                values.methodType === METHOD_MODIFIED_STRIP
              }
              historicRange={historicMonths}
              onHistoricRangeChange={setHistoricMonths}
              todayLineTs={
                values.methodType === METHOD_STRIP ||
                values.methodType === METHOD_MODIFIED_STRIP
                  ? moment.utc().startOf('month').valueOf()
                  : undefined
              }
            />
          ) : (
            <div className={styles.emptyChart}>
              <p className={styles.emptyText}>
                Configure pricing to see the preview
              </p>
            </div>
          )}
        </section>

        <section className={styles.whitePanel}>
          <h3 className={styles.sectionTitle}>Price Schedule</h3>
          {previewLoading ? (
            <DataTableSkeleton
              columnCount={4}
              rowCount={5}
              showHeader={false}
              showToolbar={false}
            />
          ) : preview && preview.rows.length > 0 ? (
            <PriceDeckScheduleTable
              rows={preview.rows.filter(
                (r) => r.month >= moment.utc().format('YYYY-MM-01')
              )}
            />
          ) : (
            <div className={styles.emptyTable}>
              <p className={styles.emptyText}>
                Configure pricing to see the schedule
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export { PriceDeckForm };
